import React, { useEffect, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';

/**
 * IOSModal - Modal con animazione "scale from origin" stile iOS
 * 
 * Il modal si espande dal punto in cui è stato cliccato il bottone
 * e si riduce tornando a quel punto quando chiuso
 * 
 * @param {Object} props
 * @param {boolean} props.isOpen - Se il modal è aperto
 * @param {Function} props.onClose - Callback per chiudere
 * @param {Object} props.origin - Posizione origine { x, y }
 * @param {string} props.title - Titolo del modal
 * @param {React.ReactNode} props.children - Contenuto
 * @param {string} props.size - Dimensione: 'sm', 'md', 'lg', 'xl', '2xl', '3xl', '4xl', '5xl', '6xl', '7xl', 'full'
 * @param {boolean} props.showCloseButton - Mostra bottone X
 * @param {boolean} props.closeOnBackdrop - Chiude al click sul backdrop
 * @param {string} props.className - Classi CSS aggiuntive
 */
const IOSModal = ({
    isOpen,
    onClose,
    origin = { x: window.innerWidth / 2, y: window.innerHeight / 2 },
    title,
    children,
    size = 'lg',
    showCloseButton = true,
    closeOnBackdrop = true,
    className = ''
}) => {
    const sizes = {
        sm: 'max-w-sm',      // 384px
        md: 'max-w-md',      // 448px
        lg: 'max-w-lg',      // 512px
        xl: 'max-w-xl',      // 576px
        '2xl': 'max-w-2xl',  // 672px
        '3xl': 'max-w-3xl',  // 768px
        '4xl': 'max-w-4xl',  // 896px
        '5xl': 'max-w-5xl',  // 1024px
        '6xl': 'max-w-6xl',  // 1152px
        '7xl': 'max-w-7xl',  // 1280px
        full: 'max-w-full mx-4',
    };

    // Blocca scroll quando modal è aperto
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isOpen]);

    // Gestione ESC per chiudere
    useEffect(() => {
        const handleEsc = (e) => {
            if (e.key === 'Escape' && isOpen) {
                onClose();
            }
        };
        
        window.addEventListener('keydown', handleEsc);
        return () => window.removeEventListener('keydown', handleEsc);
    }, [isOpen, onClose]);

    // Calcola transform origin in percentuale per CSS
    // Questo permette al modal di crescere DAL punto cliccato
    const transformOriginX = `${origin.x}px`;
    const transformOriginY = `${origin.y}px`;

    return (
        <AnimatePresence mode="wait">
            {isOpen && (
                <>
                    {/* Backdrop con fade */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                        onClick={closeOnBackdrop ? onClose : undefined}
                        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
                        style={{ WebkitBackdropFilter: 'blur(8px)' }}
                    />

                    {/* Modal Container */}
                    <div className="fixed inset-0 flex items-center justify-center z-50 p-4 pointer-events-none">
                        <motion.div
                            initial={{ 
                                scale: 0,
                                opacity: 0,
                            }}
                            animate={{ 
                                scale: 1,
                                opacity: 1,
                            }}
                            exit={{ 
                                scale: 0,
                                opacity: 0,
                            }}
                            transition={{ 
                                duration: 0.4,
                                ease: [0.16, 1, 0.3, 1],
                            }}
                            style={{
                                transformOrigin: `${transformOriginX} ${transformOriginY}`,
                            }}
                            onClick={(e) => e.stopPropagation()}
                            className={`
                                pointer-events-auto
                                flex h-[90vh] max-h-[90vh] w-full flex-col overflow-hidden
                                rounded-2xl border border-gray-200 bg-white shadow-2xl
                                dark:border-white/10 dark:bg-gray-900 dark:shadow-black/35
                                ${sizes[size]} 
                                ${className}
                            `}
                        >
                            {/* Header */}
                            {(title || showCloseButton) && (
                                <div className="flex shrink-0 items-center justify-between border-b border-gray-200 bg-[#dad8e5] px-6 py-4 dark:border-white/10 dark:bg-gray-800">
                                    {title && (
                                        <h3 className="text-xl font-bold text-gray-900 dark:font-open-sans dark:text-white">
                                            {title}
                                        </h3>
                                    )}
                                    <div className="flex-1" />
                                    {showCloseButton && (
                                        <motion.button
                                            whileHover={{ rotate: 90, scale: 1.1 }}
                                            whileTap={{ scale: 0.95 }}
                                            onClick={onClose}
                                            className="
                                                rounded-lg p-2 text-gray-600 transition-colors
                                                hover:bg-black/5 dark:text-white/90 dark:hover:bg-white/10
                                            "
                                            aria-label="Chiudi"
                                        >
                                            <X size={20} />
                                        </motion.button>
                                    )}
                                </div>
                            )}

                            {/* Content */}
                            <div className="flex min-h-0 flex-1 flex-col overflow-y-auto bg-white dark:bg-gray-900">
                                {children}
                            </div>
                        </motion.div>
                    </div>
                </>
            )}
        </AnimatePresence>
    );
};

/**
 * IOSModalSimple - Versione semplificata senza header
 */
export const IOSModalSimple = ({
    isOpen,
    onClose,
    origin,
    children,
    size = 'md',
    closeOnBackdrop = true,
}) => {
    const sizes = {
        sm: 'max-w-sm',
        md: 'max-w-md',
        lg: 'max-w-lg',
        xl: 'max-w-xl',
        '2xl': 'max-w-2xl',
    };

    const transformOriginX = `${origin?.x || window.innerWidth / 2}px`;
    const transformOriginY = `${origin?.y || window.innerHeight / 2}px`;

    return (
        <AnimatePresence>
            {isOpen && (
                <>
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={closeOnBackdrop ? onClose : undefined}
                        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
                    />

                    <div className="fixed inset-0 flex items-center justify-center z-50 p-4 pointer-events-none">
                        <motion.div
                            initial={{ 
                                scale: 0,
                                opacity: 0,
                            }}
                            animate={{ 
                                scale: 1,
                                opacity: 1,
                            }}
                            exit={{ 
                                scale: 0,
                                opacity: 0,
                            }}
                            transition={{ 
                                duration: 0.4,
                                ease: [0.16, 1, 0.3, 1],
                            }}
                            style={{
                                transformOrigin: `${transformOriginX} ${transformOriginY}`,
                            }}
                            onClick={(e) => e.stopPropagation()}
                            className={`
                                pointer-events-auto w-full rounded-2xl border border-gray-200 bg-white shadow-2xl
                                dark:border-white/10 dark:bg-gray-900 dark:shadow-black/35
                                ${sizes[size]} 
                            `}
                        >
                            {children}
                        </motion.div>
                    </div>
                </>
            )}
        </AnimatePresence>
    );
};

/**
 * Hook wrapper per gestire modal con animazione iOS
 * Semplifica l'uso combinando useModalAnimation con state management
 */
export const useIOSModal = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [origin, setOrigin] = useState({ x: 0, y: 0 });

    const openModal = useCallback((event) => {
        if (event?.currentTarget) {
            const button = event.currentTarget;
            const rect = button.getBoundingClientRect();
            
            setOrigin({
                x: rect.left + rect.width / 2,
                y: rect.top + rect.height / 2,
            });
        } else {
            // Fallback al centro
            setOrigin({
                x: window.innerWidth / 2,
                y: window.innerHeight / 2,
            });
        }
        
        setIsOpen(true);
    }, []);

    const closeModal = useCallback(() => {
        setIsOpen(false);
    }, []);

    return {
        isOpen,
        origin,
        openModal,
        closeModal,
    };
};

export default IOSModal;

